// Eskom PARCC — route capability guard.
//
// Gates a screen on the capability model: the router wraps each route in
// <RequireRoute routeKey={path}> and only renders it when canAccessRoute(path,
// grant) holds — the same predicate Nav filters on, so a deep link or stale URL
// cannot bypass nav filtering. Sits *inside* the AuthGate, so by the time it
// renders the user is normally signed in; the no-user branch is belt-and-braces.
import type { ReactNode } from 'react';
import { useAuth } from '../../lib/auth';
import { canAccessRoute, type RouteKey } from '../../lib/permissions';

export function RequireRoute({
  routeKey,
  children,
}: {
  routeKey: RouteKey;
  children: ReactNode;
}): React.JSX.Element | null {
  const { user, grant, loading } = useAuth();

  if (loading) {
    return <div className="empty">Checking permissions…</div>;
  }

  // Unauthenticated: the AuthGate owns the sign-in experience, so render nothing.
  if (!user) {
    return null;
  }

  if (!canAccessRoute(routeKey, grant)) {
    return (
      <div className="callout">
        <strong>Access denied.</strong> You do not have permission to view this section.
      </div>
    );
  }

  return <>{children}</>;
}
